import * as React from 'react';
import { useHistory } from 'react-router';
import './SuperAdminPage.css';
import {
  useGetDeviceCountLimitLazyQuery,
  useUpdateDeviceCountLimitMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ToastContext } from '../components/Toast';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { FormActions } from '../components/FormActions';
import { PageHeader } from '../components/PageHeader';
import { Input } from '../components/Input';
import { useValidation } from '../utils/Validation';

const PAGE_TITLE = '制限台数設定';

/** 制限台数はサーバーで整数として扱うため、0以上の整数だけを通す */
function validateQuantity(value: string): string | undefined {
  if (value === '') {
    return '必須入力項目です';
  }
  if (!/^\d+$/.test(value)) {
    return '0以上の整数で入力してください';
  }
  return undefined;
}

/** 台数にカウントする期間はサーバーで小数として扱うため、0以上の数値（小数を含む）だけを通す */
function validateEffectiveDays(value: string): string | undefined {
  if (value === '') {
    return '必須入力項目です';
  }
  if (!/^\d+(\.\d+)?$/.test(value)) {
    return '0以上の数値で入力してください';
  }
  return undefined;
}

const SUPER_ADMIN_RULES = {
  quantity: validateQuantity,
  effectiveDays: validateEffectiveDays,
};

/**
 * スーパー管理者向けの制限台数設定。メニューの「制限台数設定」から開く。
 */
export const SuperAdminPage: React.FC = function () {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation(SUPER_ADMIN_RULES);
  const [quantity, setQuantity] = validation.useState('quantity', '');
  const [effectiveDays, setEffectiveDays] = validation.useState('effectiveDays', '');
  const [updateLimit, updateLimitResult] = useUpdateDeviceCountLimitMutation({
    onCompleted() {
      toastContext.showToast('制限台数設定を保存しました', 'success');
    },
    onError() {
      toastContext.showToast('制限台数設定の保存に失敗しました', 'error');
    },
  });

  const [
    getDeviceCountLimit,
    getDeviceCountLimitResult,
  ] = useGetDeviceCountLimitLazyQuery({
    onCompleted(data) {
      const limit = data.getDeviceCountLimit;
      setQuantity(limit.quantity.toString());
      setEffectiveDays(limit.effective_days.toString());
    },
  });

  if (!getDeviceCountLimitResult.called) {
    getDeviceCountLimit();
  }

  if (getDeviceCountLimitResult.error) {
    return <GraphQLErrorDialog error={getDeviceCountLimitResult.error} />;
  }

  if (getDeviceCountLimitResult.loading || !getDeviceCountLimitResult.data) {
    return <Loading />;
  }

  const submit = function () {
    updateLimit({
      variables: {
        input: {
          quantity: parseInt(quantity, 10),
          effective_days: parseFloat(effectiveDays),
        },
      },
    });
  };

  return (
    <div className="SuperAdminPage">
      <PageHeader
        title={PAGE_TITLE}
        breadcrumbs={[{ label: '管理画面' }, { label: PAGE_TITLE }]}
      />
      <div className="SuperAdminPage-form">
        <Input
          label="制限台数"
          type="number"
          required={true}
          min={0}
          step={1}
          onChange={(event) => setQuantity(event.target.value)}
          value={quantity}
          validationMessage={validation.get('quantity')}
        />
        <Input
          label="台数にカウントする期間（日）"
          type="number"
          required={true}
          min={0}
          onChange={(event) => setEffectiveDays(event.target.value)}
          value={effectiveDays}
          validationMessage={validation.get('effectiveDays')}
        />
        <FormActions
          submitLabel="保存"
          submitDisabled={validation.isInvalid || updateLimitResult.loading}
          onSubmit={submit}
          // この画面には元の一覧がないので、台数の設定とつながる台数使用状況の画面へ戻る。入力した値は保存しない
          onCancel={() => history.push('/admin/devices')}
        />
      </div>
    </div>
  );
};
